<script setup lang="ts">
/**
 * Card header de la page adhésion : badge, titre, organisation, localisation,
 * prix + durée + places restantes, description expandable.
 */
import type { MembershipPlanPublic } from "~/types/membership";

const { formatCurrency } = useFormatLangue();

const props = defineProps<{
  plan: MembershipPlanPublic;
  durationLabel: string;
  isFreePricing: boolean;
  isFlexiblePricing: boolean;
  planPrice: number;
  originalPlanPrice: number;
  hasProrateDiscount: boolean;
  prorateInfo: { explanation: string; proratedPrice: number; originalPrice: number } | null;
  /**
   * Titre du GROUPE (sans nom de tarif) à afficher dans le H1. En multi-tarif,
   * `plan.title` vaut `"{groupe} — {tarif}"` : on affiche `displayTitle` (le
   * groupe seul) pour ne pas coller un tarif au titre. Fallback `plan.title`.
   */
  displayTitle?: string;
}>();

const headerTitle = computed(() => props.displayTitle?.trim() || props.plan.title);

const localePath = useLocalePath();

/** Page de l'association, dans la langue du visiteur (null sans adresse connue). */
const lienAssociation = computed(() =>
  props.plan.organization_slug
    ? localePath({ path: `/associations/${props.plan.organization_slug}` })
    : null,
);
</script>

<template>
  <div class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] p-4 md:p-6">
    <div class="flex items-start justify-between gap-4">
      <div class="flex-1 min-w-0">
        <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 mb-2">
          <UIcon name="i-lucide-id-card" class="size-3" />
          {{ $t('adhesion.aslugHeaderCard.badgeAdhesion') }}
        </span>
        <h1 class="text-xl md:text-3xl font-bold text-[#2d2e5c] dark:text-white leading-tight">
          {{ headerTitle }}
        </h1>
        <p class="text-sm md:text-base text-[#717198] dark:text-gray-400 mt-1">
          {{ $t('adhesion.aslugHeaderCard.parOrganisation') }}
          <!-- Lien vers la page de l'association, comme sur la cagnotte et
               la boutique. Nouvel onglet : l'adhésion en cours n'est pas
               perdue. Sans adresse connue, texte simple plutôt qu'un lien mort. -->
          <a
            v-if="lienAssociation"
            :href="lienAssociation"
            target="_blank"
            rel="noopener"
            class="font-medium text-[#212121] dark:text-white hover:underline underline-offset-2"
          >{{ plan.organization_name || $t('adhesion.aslugHeaderCard.organisationParDefaut') }}</a>
          <span v-else class="font-medium text-[#212121] dark:text-white">{{ plan.organization_name || $t('adhesion.aslugHeaderCard.organisationParDefaut') }}</span>
        </p>
      </div>
    </div>

    <!-- Adresse : retirée du bloc titre (doublon) — elle vit uniquement dans la
         sidebar de droite (AslugSidebar), comme la page billetterie (le header
         événement n'affiche pas l'adresse non plus). Karim 2026-07-08. -->

    <!-- Price + Duration -->
    <div class="flex flex-wrap items-center gap-3 mt-4">
      <div class="inline-flex items-center gap-2">
        <span v-if="hasProrateDiscount" class="text-sm font-medium text-gray-400 line-through">
          {{ formatCurrency(originalPlanPrice) }}
        </span>
        <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl accent-btn text-sm font-bold">
          <template v-if="isFreePricing">{{ $t('adhesion.aslugHeaderCard.gratuit') }}</template>
          <template v-else-if="isFlexiblePricing">{{ $t('adhesion.aslugHeaderCard.tarifLibre') }}</template>
          <template v-else-if="planPrice === 0">{{ $t('adhesion.aslugHeaderCard.gratuit') }}</template>
          <template v-else>{{ formatCurrency(planPrice) }}</template>
        </div>
      </div>
      <div v-if="hasProrateDiscount && prorateInfo" class="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
        <UIcon name="i-lucide-calculator" class="size-3.5" />
        <span>{{ prorateInfo.explanation }}</span>
      </div>
      <div v-if="durationLabel" class="inline-flex items-center gap-1.5 text-sm text-[#505870] dark:text-gray-400">
        <UIcon name="i-lucide-clock" class="size-4" />
        <span>{{ durationLabel }}</span>
      </div>
      <div v-if="plan.max_members" class="inline-flex items-center gap-1.5 text-sm text-[#505870] dark:text-gray-400">
        <UIcon name="i-lucide-users" class="size-4" />
        <span>
          {{ $t('adhesion.aslugHeaderCard.placesRestantes', { n: plan.max_members - (plan.member_count || 0) }) }}
        </span>
      </div>
    </div>

    <!-- Description -->
    <PublicPageDescriptionExpandable v-if="plan.description" :html="plan.description" />
  </div>
</template>

<style scoped>
/* Boutons d'action — couleur choisie dans la configuration de l'adhésion.
   Repli #212121 quand aucune couleur n'est posée. Même classe et mêmes
   variables que la billetterie (BslugStepRecap) : une seule règle pour les
   trois domaines. Karim 2026-09-12. */
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
